import { Box, SimpleLabel, Svg, Text } from '@hopskipdrive/react-components';
import { format } from 'date-fns';

import { PICKUP } from '../../../constants';
import { DriverEvent } from '../../components/DriverEvent';
import { TimeDifferenceTag } from '../../components/TimeDifferenceTag';

export const DepartureEvent = ({
  waypoint,
  waypointType,
  driver,
}: {
  waypoint?: RideDetailsWaypoint;
  waypointType: string;
  driver: Driver | null;
}) => {
  if (!driver || !waypoint?.estimatedDepartsAt) {
    return null;
  }

  return (
    <DriverEvent
      title={`${waypoint.actualDepartsAt ? 'Drove' : 'Drive'} to ${waypointType}`}
      isComplete={!!waypoint.actualDepartsAt}
      formattedTime={`${!waypoint.actualDepartsAt ? '~' : ''}${format(
        waypoint.actualDepartsAt || waypoint.estimatedDepartsAt,
        'hh:mm aaa'
      )}`}
      timeDifference={
        waypoint.actualDepartsAt ? (
          <TimeDifferenceTag timeA={waypoint.actualDepartsAt} timeB={waypoint.estimatedDepartsAt} />
        ) : null
      }
    />
  );
};

export const CareDriverCheckedInEvent = ({
  driver,
  checkedInAt,
}: {
  driver: Driver | null;
  checkedInAt: Nullable<Date>;
}) => {
  if (!driver || !checkedInAt) {
    return null;
  }

  return (
    <DriverEvent title={'CareDriver checked in'} isComplete={true} formattedTime={format(checkedInAt, 'hh:mm aaa')} />
  );
};

export const ArrivalEvent = ({
  waypoint,
  waypointIndex,
  waypointType,
  driver,
  timelinessStatus,
  estimatedStartsAt,
  startsAt,
}: {
  waypoint?: RideDetailsWaypoint;
  waypointIndex: number;
  waypointType: string;
  driver: Driver | null;
  timelinessStatus: string;
  startsAt: Nullable<Date>;
  estimatedStartsAt?: Nullable<Date>;
}) => {
  return (
    <>
      <ArrivalEventText hasArrived={!!waypoint?.actualArrivesAt} waypointType={waypointType} />
      <ArrivalEventTime
        waypoint={waypoint}
        waypointIndex={waypointIndex}
        driver={driver}
        timelinessStatus={timelinessStatus}
        estimatedStartsAt={estimatedStartsAt}
        startsAt={startsAt}
      />
    </>
  );
};

export const CareDriverStartedEvent = ({ driver, startedAt }: { driver: Driver | null; startedAt: Nullable<Date> }) => {
  if (!driver || !startedAt) {
    return null;
  }

  return (
    <DriverEvent title={'CareDriver on their way'} isComplete={true} formattedTime={format(startedAt, 'hh:mm aaa')} />
  );
};

export const CompletedRideEvent = ({
  driver,
  endsAt,
  completedAt,
}: {
  driver: Driver | null;
  endsAt: Nullable<Date>;
  completedAt: Nullable<Date>;
}) => {
  if (!driver || !endsAt) {
    return null;
  }

  return (
    <DriverEvent
      title={`${completedAt ? 'Completed' : 'Complete'} ride`}
      isLast={true}
      isComplete={!!completedAt}
      formattedTime={`${!completedAt ? '~' : ''}${format(completedAt || endsAt, 'hh:mm aaa')}`}
      timeDifference={<TimeDifferenceTag timeA={completedAt || null} timeB={endsAt} />}
    />
  );
};

const ArrivalEventText = ({ hasArrived, waypointType }: { hasArrived: boolean; waypointType: string }) => {
  return (
    <Text variant="h3" textColor={hasArrived ? 'secondaryText' : ''}>{`${hasArrived ? 'Arrived' : 'Arrive'} at ${
      waypointType === PICKUP ? 'pickup' : 'drop-off'
    }`}</Text>
  );
};

const ArrivalEventTime = ({
  waypoint,
  waypointIndex,
  driver,
  timelinessStatus,
  estimatedStartsAt,
  startsAt,
}: {
  waypoint?: RideDetailsWaypoint;
  driver: Driver | null;
  waypointIndex: number;
  timelinessStatus: string;
  estimatedStartsAt?: Nullable<Date>;
  startsAt: Nullable<Date>;
}) => {
  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  if (waypointIndex === 0 && (timelinessStatus === 'late' || timelinessStatus === 'delayed')) {
    return (
      <Box display="flex" alignItems="center" gap="8px">
        <SimpleLabel label={capitalizeFirstLetter(timelinessStatus)} textColor="orange" bgColor="orange95" />

        {estimatedStartsAt && (
          <>
            <Text textColor="neutral70">~ </Text>
            <Text textColor="neutral70" sx={{ textDecoration: 'line-through' }}>
              {startsAt ? format(new Date(startsAt), 'hh:mm aaa') : null}
            </Text>
            <Text textColor="warning-dark">
              {estimatedStartsAt ? format(new Date(estimatedStartsAt), 'hh:mm aaa') : null}
            </Text>
          </>
        )}
      </Box>
    );
  }
  if (!driver && waypoint?.estimatedArrivesAt) {
    return <Text>{`~${format(waypoint.estimatedArrivesAt, 'hh:mm aaa')}`}</Text>;
  }
  if (!driver) {
    return null;
  }

  if (waypoint?.actualArrivesAt) {
    return (
      <Box display="flex" alignItems="center" gap="8px">
        <TimeDifferenceTag timeA={waypoint.actualArrivesAt} timeB={waypoint?.estimatedArrivesAt} />
        <Svg icon="checkmark-stroke" width={12} />
        <Text textColor="secondaryText">{format(waypoint.actualArrivesAt, 'hh:mm aaa')}</Text>
      </Box>
    );
  }

  return waypoint?.estimatedArrivesAt ? <Text>{`~${format(waypoint.estimatedArrivesAt, 'hh:mm aaa')}`}</Text> : null;
};
